import { useState, useCallback, useEffect } from "react"
import { makeBackendRequest } from "./use-backend-api"
import type { QuizSession, Question, UserProgress } from "../types/quiz"
import { useAuth } from "../contexts/auth-context"

export function useQuiz() {
  const [session, setSession] = useState<QuizSession | null>(null)
  const [resultsSaved, setResultsSaved] = useState(false)
  const { isAuthenticated, user } = useAuth()

  // Initialize user progress from auth context or default values
  const [userProgress, setUserProgress] = useState<UserProgress>(() => {
    if (user?.performance) {
      return {
        totalPoints: user.performance.totalPoints,
        completedExams: [], // This would need to come from a separate API call if needed
        questionsAnswered: user.performance.questionAnsweredCount,
        correctAnswers: user.performance.correctAnswersCount,
      }
    }
    return {
      totalPoints: 0,
      completedExams: [],
      questionsAnswered: 0,
      correctAnswers: 0,
    }
  })

  // Update user progress when user data changes (e.g., after login)
  useEffect(() => {
    if (user?.performance) {
      setUserProgress((prev) => ({
        ...prev,
        totalPoints: user.performance!.totalPoints,
        questionsAnswered: user.performance!.questionAnsweredCount,
        correctAnswers: user.performance!.correctAnswersCount,
      }))
    } else if (!isAuthenticated) {
      // Reset to default values when user logs out
      setUserProgress({
        totalPoints: 0,
        completedExams: [],
        questionsAnswered: 0,
        correctAnswers: 0,
      })
    }
  }, [user?.performance, isAuthenticated])

  const startQuiz = useCallback((questions: Question[], mode: "exam" | "random", examId?: string) => {
    setSession({
      questions,
      currentQuestionIndex: 0,
      answers: new Array(questions.length).fill(null),
      score: 0,
      isComplete: false,
      mode,
      examId,
    })
    setResultsSaved(false) // Reset results saved state for new quiz
  }, [])

  // Effect to handle saving results when quiz is complete and user is authenticated
  useEffect(() => {
    const saveResultsWhenComplete = async () => {
      if (session?.isComplete && isAuthenticated && !resultsSaved) {
        try {
          console.log("Quiz completed and user is authenticated, saving results...")
          await saveQuizResults(session)
          setResultsSaved(true)
        } catch (error) {
          console.error("Failed to save quiz results:", error)
        }
      }
    }

    saveResultsWhenComplete()
  }, [session?.isComplete, isAuthenticated, resultsSaved])

  // Helper function to save quiz results
  const saveQuizResults = async (quizSession: QuizSession) => {
    const bulkResponses = quizSession.questions.map((question, index) => {
      const userAnswer = quizSession.answers[index]
      const isCorrect = userAnswer ? isAnswerCorrect(question, userAnswer) : false
      const pointsEarned = isCorrect ? question.points : 0

      return {
        question_id: question.id,
        answers: userAnswer || [],
        points_earned: pointsEarned,
        quizzable_id: quizSession.examId || null,
        quizzable_type: quizSession.mode === "exam" ? "Exam" : "Quiz",
      }
    })

    const payload = {
      responses: bulkResponses,
    }

    const response = await makeBackendRequest("/api/responses/bulk", {
      method: "POST",
      body: JSON.stringify(payload),
    })

    if (response.success) {
      console.log(`${quizSession.mode === "exam" ? "Exam" : "Random quiz"} responses saved successfully`)

      // Mark exam as completed if it's an exam
      if (quizSession.mode === "exam" && quizSession.examId) {
        setUserProgress((prev) => ({
          ...prev,
          completedExams: [...prev.completedExams, quizSession.examId!],
        }))
      }
    }

    return response
  }

  const answerQuestion = useCallback(
    (answer: string[]) => {
      if (!session) return

      const newAnswers = [...session.answers]
      newAnswers[session.currentQuestionIndex] = answer

      const currentQuestion = session.questions[session.currentQuestionIndex]

      // Check if answer is correct based on question type
      let isCorrect = false

      if (currentQuestion.type === "multiple-choice") {
        // For multiple choice, check if arrays have same elements (order doesn't matter)
        if (answer.length === currentQuestion.correctAnswers.length) {
          isCorrect = answer.every((ans) => currentQuestion.correctAnswers.includes(ans))
        }
      } else {
        // For fill-in-blank, check exact match in order (case insensitive)
        if (answer.length === currentQuestion.correctAnswers.length) {
          isCorrect = answer.every(
            (ans, index) => ans.toLowerCase() === currentQuestion.correctAnswers[index].toLowerCase(),
          )
        }
      }

      const newScore = isCorrect ? session.score + currentQuestion.points : session.score

      setSession((prev) =>
        prev
          ? {
            ...prev,
            answers: newAnswers,
            score: newScore,
          }
          : null,
      )

      // Update local user progress (this will be synced with server when quiz completes)
      setUserProgress((prev) => ({
        ...prev,
        questionsAnswered: prev.questionsAnswered + 1,
        correctAnswers: isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers,
        totalPoints: isCorrect ? prev.totalPoints + currentQuestion.points : prev.totalPoints,
      }))
    },
    [session],
  )

  const nextQuestion = useCallback(() => {
    if (!session) return

    if (session.currentQuestionIndex < session.questions.length - 1) {
      setSession((prev) =>
        prev
          ? {
            ...prev,
            currentQuestionIndex: prev.currentQuestionIndex + 1,
          }
          : null,
      )
    } else {
      // Quiz complete - just mark as complete, let useEffect handle saving
      console.log("Quiz completed, marking session as complete...")
      const completedSession = {
        ...session,
        isComplete: true,
      }

      setSession(completedSession)
    }
  }, [session])

  // Helper function to check if an answer is correct
  const isAnswerCorrect = (question: Question, userAnswer: string[]): boolean => {
    if (question.type === "multiple-choice") {
      // For multiple choice, check if arrays have same elements (order doesn't matter)
      if (userAnswer.length !== question.correctAnswers.length) return false
      return userAnswer.every((answer) => question.correctAnswers.includes(answer))
    } else {
      // For fill-in-blank, check exact match in order (case insensitive)
      if (userAnswer.length !== question.correctAnswers.length) return false
      return userAnswer.every((answer, index) => answer.toLowerCase() === question.correctAnswers[index].toLowerCase())
    }
  }

  const resetQuiz = useCallback(() => {
    setSession(null)
    setResultsSaved(false)
  }, [])

  return {
    session,
    userProgress,
    startQuiz,
    answerQuestion,
    nextQuestion,
    resetQuiz,
    resultsSaved, // Expose this for debugging/testing
  }
}
